import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { filterJobs, KdsAggsviews } from "../../../config/Services";
import { getUtcDayRange } from "../../../utils/jobDateRange";
import { getSelectedSlotId, subscribeSlots } from "../../../utils/odsArchitecture/odsSlotService";
import { aggregateKitchenItems, loadKitchenWorkload, normalizeAcceptedKitchenJob } from "./itemWorkload";
import { loadAggregatedKitchenWorkload } from "./aggregatedWorkload";
import { useKitchenNotifications } from "../../../context/KitchenNotificationsContext";

// Order-level sort menu value → filterJobs sort payload.
const SORT_PAYLOADS = {
  due: { sortByDue: "ASCENDING" },
  oldest: { sortByAcceptedAt: "ASCENDING" },
  newest: { sortByAcceptedAt: "DESCENDING" },
  quickest: { sortByPrepTime: "ASCENDING" },
};

export default function useItemWorkload({
  enabled, orgId, locId, query, selectedDate, selectedSources, selectedPrepStations, aggregationType = null, sort = null,
}) {
  const [slotId, setSlotId] = useState(getSelectedSlotId);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [revision, setRevision] = useState(0);
  const readGeneration = useRef(0);
  const { registerWorkload, scope: notificationScope } = useKitchenNotifications();
  const notificationFeed = useRef(null);
  // Claim polling before the provider's passive effect can issue an extra Pending read.
  useLayoutEffect(() => {
    if (!enabled || !orgId || !locId) return;
    const feed = registerWorkload({ orgId, locationId: locId });
    notificationFeed.current = feed;
    return () => {
      notificationFeed.current = null;
      feed.dispose();
    };
  }, [enabled, orgId, locId, notificationScope, registerWorkload]);
  const refresh = useCallback(() => {
    readGeneration.current += 1;
    setRevision((value) => value + 1);
  }, []);
  const sourcesKey = JSON.stringify(selectedSources || []);
  const stationsKey = JSON.stringify(selectedPrepStations || []);
  const groups = useMemo(() => aggregateKitchenItems(jobs, JSON.parse(stationsKey)), [jobs, stationsKey]);
  const scopeRef = useRef("");
  const applyPendingDecision = useCallback(({ jobIds, status }) => {
    const ids = new Set((jobIds || []).map(String));
    setJobs((previous) => status === "REJECTED"
      ? previous.filter((job) => !ids.has(String(job.jobId)))
      : previous.map((job) => ids.has(String(job.jobId))
        ? normalizeAcceptedKitchenJob({ ...job, status }) : job));
    setLastUpdated(new Date());
    refresh();
  }, [refresh]);
  const loadWorkload = useCallback((filters, isCurrent = () => true) => aggregationType
    ? loadAggregatedKitchenWorkload(KdsAggsviews, filters, aggregationType, undefined, isCurrent)
    : loadKitchenWorkload(filterJobs, { ...filters, ...SORT_PAYLOADS[sort] }, isCurrent), [aggregationType, sort]);
  // Full job/cart identity is not present in aggregates. Read it only for actions.
  const getFreshJobs = useCallback(async () => {
    const scope = scopeRef.current;
    const fresh = await loadKitchenWorkload(filterJobs, {
    orgId, locationId: locId,
    ...getUtcDayRange(selectedDate),
    ...(query?.trim() ? { query: query.trim() } : {}),
    ...(JSON.parse(sourcesKey).length ? { source: JSON.parse(sourcesKey) } : {}),
    ...(JSON.parse(stationsKey).length ? { prepStationIds: JSON.parse(stationsKey) } : {}),
    ...(slotId && slotId !== "ALL" ? { slotIds: [slotId] } : {}),
    }, () => scopeRef.current === scope);
    if (scopeRef.current !== scope) throw new Error("Kitchen filters or location changed. Please reopen the action.");
    return fresh;
  }, [orgId, locId, query, selectedDate, sourcesKey, stationsKey, slotId]);

  useEffect(() => subscribeSlots(() => setSlotId(getSelectedSlotId())), []);

  useEffect(() => {
    if (!enabled) {
      // Idle (e.g. Tables view with no occupied table): drop the previous board.
      scopeRef.current = "";
      setJobs([]);
      setError("");
      setLastUpdated(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    let inFlight = false;
    const sources = JSON.parse(sourcesKey);
    const stations = JSON.parse(stationsKey);
    const fetchWorkload = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      const generation = readGeneration.current;
      setLoading(true);
      try {
        const jobs = await loadWorkload({
          orgId,
          locationId: locId,
          ...getUtcDayRange(selectedDate),
          ...(query?.trim() ? { query: query.trim() } : {}),
          ...(sources.length ? { source: sources } : {}),
          ...(stations.length ? { prepStationIds: stations } : {}),
          ...(slotId && slotId !== "ALL" ? { slotIds: [slotId] } : {}),
        }, () => !cancelled && generation === readGeneration.current);
        if (!cancelled && generation === readGeneration.current) {
          notificationFeed.current?.publish(jobs);
          setJobs(stations.length ? jobs.map((job) => ({
            ...job,
            tasks: (job.tasks || []).filter((task) => stations.includes(task.metadata?.prepStationId)),
          })) : jobs);
          setError("");
          setLastUpdated(new Date());
        }
      } catch (err) {
        if (!cancelled && generation === readGeneration.current) {
          notificationFeed.current?.fail(err);
          setJobs([]);
          setError(err?.message || "Unable to load kitchen workload. Please retry.");
        }
      } finally {
        inFlight = false;
        if (!cancelled && generation === readGeneration.current) setLoading(false);
      }
    };
    const scope = JSON.stringify([orgId, locId, query, selectedDate, sourcesKey, stationsKey, slotId, aggregationType, notificationScope]);
    if (scopeRef.current !== scope) {
      setJobs([]);
      setError("");
      setLastUpdated(null);
      scopeRef.current = scope;
    }
    setLoading(true);
    // Match global-search debounce; invalidate old filter responses immediately.
    const debounceTimer = setTimeout(fetchWorkload, query ? 500 : 0);
    const pollTimer = setInterval(fetchWorkload, 60000);
    return () => {
      cancelled = true;
      clearTimeout(debounceTimer);
      clearInterval(pollTimer);
    };
  }, [enabled, orgId, locId, query, selectedDate, sourcesKey, stationsKey, slotId, revision, aggregationType, loadWorkload, notificationScope]);

  return { groups, jobs, loading, error, lastUpdated, refresh, getFreshJobs, applyPendingDecision };
}
